import { useEffect, useRef, useState } from 'react';

/**
 * Lays a mock website out at a real page width and zooms it to fit its frame,
 * like a screenshot — so the widget renderer inside picks the column count a
 * real site of that width would get, instead of cramming into a narrow box.
 *
 * Below `breakpoint` the page is laid out at `narrow` instead of `wide`: a
 * phone should see a phone-width site, not a desktop page shrunk until the
 * reviews are unreadable.
 *
 * `zoom`, not `transform: scale`: the renderer measures its container to pick
 * a column count, and a transform shrinks what it measures.
 */
export function useFitZoom({
  wide,
  narrow,
  breakpoint = 640,
}: {
  wide: number;
  narrow: number;
  breakpoint?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ zoom: 0.75, canvas: wide });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const canvas = el.clientWidth < breakpoint ? narrow : wide;
      setFit({ zoom: el.clientWidth / canvas, canvas });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [wide, narrow, breakpoint]);
  return { ref, ...fit, isNarrow: fit.canvas === narrow };
}
